/**
 * Mobile-Optimized Dashboard Component
 * Ultra-compact design for mobile devices
 */

import React, { useState } from 'react';
import { useItems } from '../../contexts/ItemsContext';
import { useAuth } from '../../contexts/AuthContext';
import MobileUIOptimized from '../Mobile/MobileUIOptimized';
import MobileModal from '../Mobile/MobileModal';
import ItemTypeModal from '../Items/ItemTypeModal';
import PasswordForm from '../Password/PasswordForm';
import CreditCardForm from '../CreditCard/CreditCardForm';
import ApiKeyForm from '../ApiKey/ApiKeyForm';
import PasswordShareModal from '../Password/PasswordShareModal';
import { Modal } from '../UI';
import './DashboardMobile.css';

function DashboardMobile({ onMenuAction }) {
  const { user } = useAuth();
  const { 
    allItems,
    stats,
    loading,
    addItem,
    updateItem,
    deleteItem
  } = useItems();
  
  const [showItemTypeModal, setShowItemTypeModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('passwords');
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingItem, setSharingItem] = useState(null);

  // Handle FAB click
  const handleAddClick = () => {
    setShowItemTypeModal(true);
  };

  // Handle item type selection
  const handleItemTypeSelect = (type) => {
    setShowItemTypeModal(false);
    setFormType(type);
    setEditingItem(null);
    setShowForm(true);
  };
  
  // Handle item click for editing
  const handleItemClick = (item) => {
    setEditingItem(item);
    setFormType(item.itemType);
    setShowForm(true);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle share
  const handleShare = (item) => {
    setSharingItem(item);
    setShowShareModal(true);
  };

  // Handle share modal close
  const handleShareModalClose = () => {
    setShowShareModal(false);
    setSharingItem(null);
  };

  // Handle save
  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await updateItem(editingItem.id, data, editingItem.itemType);
      } else {
        await addItem(formType, data);
      }
      setShowForm(false);
      setEditingItem(null);
      setFormType(null);
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장에 실패했습니다.');
    }
  };

  // Get form component based on type
  const getFormComponent = () => {
    // Map itemType to form type for consistency
    const type = formType === 'creditcard' ? 'creditCard' : 
                 formType === 'apikey' ? 'apiKey' :
                 formType;
                 
    switch (type) {
      case 'creditCard':
        return (
          <MobileModal 
            isOpen={showForm}
            onClose={() => {
              setShowForm(false);
              setEditingItem(null);
              setFormType(null);
            }}
            title={editingItem ? '신용카드 수정' : '신용카드 추가'}
          >
            <CreditCardForm
              onSubmit={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingItem(null);
                setFormType(null);
              }}
              loading={false}
              editingCard={editingItem}
              hideHeader={true}
            />
          </MobileModal>
        );
      case 'apiKey':
        return (
          <MobileModal 
            isOpen={showForm}
            onClose={() => {
              setShowForm(false);
              setEditingItem(null);
              setFormType(null);
            }}
            title={editingItem ? 'API 키 수정' : 'API 키 추가'}
          >
            <ApiKeyForm
              onSubmit={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingItem(null);
                setFormType(null);
              }}
              loading={false}
              editingApiKey={editingItem}
              hideHeader={true}
            />
          </MobileModal>
        );
      default:
        return (
          <MobileModal 
            isOpen={showForm}
            onClose={() => {
              setShowForm(false);
              setEditingItem(null);
              setFormType(null);
            }}
            title={editingItem ? '패스워드 수정' : '패스워드 추가'}
          >
            <PasswordForm
              onSubmit={handleSave}
              onCancel={() => {
                setShowForm(false);
                setEditingItem(null);
                setFormType(null);
              }}
              loading={false}
              editingPassword={editingItem}
              hideHeader={true}
            />
          </MobileModal>
        );
    }
  };

  return (
    <div className="dashboard-mobile">
      <MobileUIOptimized 
        activeTab={activeTab}
        onAddClick={handleAddClick}
        onItemClick={handleItemClick}
        onTabChange={handleTabChange}
        onShare={handleShare}
      />

      {/* Item Type Selection Modal */}
      <ItemTypeModal
        isOpen={showItemTypeModal}
        onClose={() => setShowItemTypeModal(false)}
        onSelect={handleItemTypeSelect}
      />

      {/* Form Modals */}
      {showForm && getFormComponent()}

      {/* Share Modal */}
      {showShareModal && sharingItem && (
        <PasswordShareModal
          password={sharingItem}
          isOpen={showShareModal}
          onClose={handleShareModalClose}
        />
      )}
    </div>
  );
}

export default DashboardMobile;